import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errMsg, imgUrl } from "@/lib/api";

export const PageHeader = ({ title, desc, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div>
      <p className="eyebrow !text-[#9E7B27]">Yönetim</p>
      <h1 className="font-serif-ym text-4xl font-semibold text-[#1A1513] mt-1" data-testid="admin-page-title">{title}</h1>
      {desc && <p className="text-sm text-[#6B5E56] mt-1">{desc}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ children, className = "", ...p }) => (
  <div className={`rounded-lg border border-[#E5DCD0] bg-white ${className}`} {...p}>{children}</div>
);

export const DarkBtn = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center justify-center gap-2 rounded-md bg-[#1A1513] text-[#FDFBF7] px-4 py-2.5 text-sm font-medium hover:bg-[#2A1C18] transition-colors disabled:opacity-50 ${className}`}>
    {children}
  </button>
);

export function ConfirmDelete({ onConfirm, children, title = "Silmek istediğinize emin misiniz?", testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testid ? `${testid}-dialog` : undefined}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-[#C84B31] hover:bg-[#a83d27]" data-testid="confirm-delete-btn">Sil</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const ref = useRef();
  const [loading, setLoading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 rounded-md border border-[#E5DCD0] bg-[#F3EDE2] overflow-hidden grid place-items-center shrink-0">
          {value ? <img src={imgUrl(value)} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <Upload size={18} className="text-[#6B5E56]" />}
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => ref.current.click()} disabled={loading} className="inline-flex items-center gap-2 rounded-md border border-[#E5DCD0] px-3 py-2 text-sm hover:bg-[#F3EDE2]" data-testid={`${testid}-btn`}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Fotoğraf yükle
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-[#C84B31]" data-testid={`${testid}-remove-btn`}><X size={12} /> Kaldır</button>
          )}
        </div>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#6B5E56]">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-[#6B5E56]">{hint}</span>}
  </label>
);

export const STATUS = {
  beklemede: ["Beklemede", "bg-amber-100 text-amber-800"],
  onaylandi: ["Onaylandı", "bg-emerald-100 text-emerald-800"],
  tamamlandi: ["Tamamlandı", "bg-sky-100 text-sky-800"],
  iptal: ["İptal", "bg-red-100 text-red-800"],
};
